import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    Shield, 
    Clock, 
    Users, 
    Calendar, 
    FileText, 
    Truck, 
    GraduationCap,
    CheckCircle,
    ArrowRight,
    BarChart3,
    Bell,
    Smartphone
} from 'lucide-react';
import { Button } from '../components/ui/button';

const FeatureCard = ({ icon: Icon, title, description }) => (
    <div className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow border border-gray-100">
        <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
            <Icon className="w-6 h-6 text-primary" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
        <p className="text-gray-600 text-sm leading-relaxed">{description}</p>
    </div>
);

const StatCard = ({ value, label }) => (
    <div className="text-center">
        <div className="text-4xl lg:text-5xl font-bold text-white mb-2">{value}</div>
        <div className="text-gray-300 text-sm">{label}</div>
    </div>
);

export default function HomePage() {
    const navigate = useNavigate();
    
    const features = [
        {
            icon: Users,
            title: "Member Management",
            description: "Track all members, their qualifications, contact details, and emergency contacts in one centralised system."
        },
        {
            icon: Clock,
            title: "Attendance Tracking",
            description: "Real-time clock in/out with automatic fatigue monitoring and comprehensive logbook history."
        },
        {
            icon: Calendar,
            title: "Activity Calendar",
            description: "Schedule training, operations, and events. Members can respond to activities and view group calendars."
        },
        {
            icon: GraduationCap,
            title: "Qualifications & Training",
            description: "Monitor member qualifications with expiry tracking and proactive alerts for renewals."
        },
        {
            icon: Truck,
            title: "Fleet & Equipment",
            description: "Track vehicles, equipment, and stores. Know exactly what's available for the next incident."
        },
        {
            icon: FileText,
            title: "Forms & Checklists",
            description: "Create custom inspection checklists and assessment forms. Digital submissions replace paper."
        }
    ];

    const benefits = [
        "Real-time visibility into team availability",
        "Automated fatigue management alerts",
        "Mobile-first design for field use",
        "Role-based access control",
        "Comprehensive audit logging",
        "Secure data encryption"
    ];

    return (
        <div className="min-h-screen bg-white">
            {/* Navigation */}
            <nav className="fixed top-0 left-0 right-0 z-50 bg-[#1a3a2f]/95 backdrop-blur-sm border-b border-white/10">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        <div className="flex items-center gap-3">
                            <img 
                                src="/react-qld-logo.png" 
                                alt="REACT QLD" 
                                className="h-10 object-contain"
                            />
                        </div>
                        <div className="hidden md:flex items-center gap-8">
                            <a href="#features" className="text-gray-300 hover:text-white text-sm font-medium transition-colors">Features</a>
                            <a href="#overview" className="text-gray-300 hover:text-white text-sm font-medium transition-colors">Overview</a>
                            <a href="#benefits" className="text-gray-300 hover:text-white text-sm font-medium transition-colors">Benefits</a>
                        </div>
                        <Button 
                            onClick={() => navigate('/login')}
                            className="bg-primary hover:bg-primary/90 text-white font-medium px-6"
                            data-testid="nav-login-btn"
                        >
                            Member Login
                        </Button>
                    </div>
                </div>
            </nav>

            {/* Hero Section */}
            <section className="relative min-h-[90vh] flex items-center bg-[#1a3a2f] overflow-hidden">
                {/* Background Pattern */}
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute inset-0" style={{
                        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                        backgroundSize: '60px 60px'
                    }} />
                </div>
                
                {/* Emergency Icons Pattern Overlay */}
                <div className="absolute inset-0 opacity-5">
                    <div className="grid grid-cols-8 gap-8 p-8 h-full">
                        {[...Array(64)].map((_, i) => (
                            <div key={i} className="flex items-center justify-center text-white">
                                {i % 4 === 0 && <Shield className="w-8 h-8" />}
                                {i % 4 === 1 && <Truck className="w-8 h-8" />}
                                {i % 4 === 2 && <Users className="w-8 h-8" />}
                                {i % 4 === 3 && <Bell className="w-8 h-8" />}
                            </div>
                        ))}
                    </div>
                </div>

                <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        <div>
                            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
                                <span className="italic">Information</span><br />
                                <span className="italic">Management for</span><br />
                                <span className="text-primary italic">Response Teams.</span>
                            </h1>
                            <p className="text-lg text-gray-300 mb-8 max-w-lg">
                                REACT QLD is an essential tool, providing you real-time insights into your responders, 
                                equipment, and training. Are you prepared for the next incident?
                            </p>
                            <div className="flex flex-col sm:flex-row gap-4">
                                <Button 
                                    size="lg"
                                    onClick={() => navigate('/login')}
                                    className="bg-primary hover:bg-primary/90 text-white font-semibold px-8 py-6 text-lg"
                                    data-testid="hero-login-btn"
                                >
                                    Member Login
                                    <ArrowRight className="ml-2 w-5 h-5" />
                                </Button>
                                <Button 
                                    size="lg"
                                    variant="outline"
                                    onClick={() => document.getElementById('features').scrollIntoView({ behavior: 'smooth' })}
                                    className="border-white/30 text-white hover:bg-white/10 font-semibold px-8 py-6 text-lg"
                                    data-testid="hero-features-btn"
                                >
                                    Explore Features
                                </Button>
                            </div>
                        </div>
                        <div className="hidden lg:block">
                            <div className="relative">
                                <div className="absolute -inset-4 bg-primary/20 rounded-2xl blur-xl"></div>
                                <img 
                                    src="/react-qld-logo.png" 
                                    alt="REACT QLD Logo" 
                                    className="relative w-full max-w-md mx-auto drop-shadow-2xl"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Scroll Indicator */}
                <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
                    <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center">
                        <div className="w-1 h-3 bg-white/50 rounded-full mt-2"></div>
                    </div>
                </div>
            </section>

            {/* Overview Section */}
            <section id="overview" className="py-20 bg-gray-50">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">Overview</h2>
                    <p className="text-lg text-gray-600 leading-relaxed">
                        REACT QLD brings an online simplified approach to traditional paper-based systems 
                        used to <strong>track, manage and engage</strong> your responders and equipment. 
                        Ensure your team is prepared for the next incident. Whatever, whenever!
                    </p>
                </div>
            </section>

            {/* Features Section */}
            <section id="features" className="py-20 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
                            Everything You Need to Manage Your Team
                        </h2>
                        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                            A comprehensive suite of tools designed specifically for emergency response organisations
                        </p>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {features.map((feature, index) => (
                            <FeatureCard key={index} {...feature} />
                        ))}
                    </div>
                </div>
            </section>

            {/* Stats Section */}
            <section className="py-20 bg-[#1a3a2f]">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
                        <StatCard value="24/7" label="Real-time Tracking" />
                        <StatCard value="100%" label="Mobile Responsive" />
                        <StatCard value="13hr" label="Auto Clock-off Safety" />
                        <StatCard value="256-bit" label="Data Encryption" />
                    </div>
                </div>
            </section>

            {/* Benefits Section */}
            <section id="benefits" className="py-20 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        <div>
                            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
                                Built for Emergency Response Teams
                            </h2>
                            <p className="text-lg text-gray-600 mb-8">
                                REACT QLD understands the unique challenges faced by volunteer emergency services. 
                                Our platform is designed from the ground up to support your operations.
                            </p>
                            <ul className="space-y-4">
                                {benefits.map((benefit, index) => (
                                    <li key={index} className="flex items-start gap-3">
                                        <CheckCircle className="w-6 h-6 text-primary flex-shrink-0 mt-0.5" />
                                        <span className="text-gray-700">{benefit}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div className="relative">
                            <div className="bg-gray-100 rounded-2xl p-8">
                                <div className="space-y-4">
                                    <div className="flex items-center gap-4 bg-white rounded-lg p-4 shadow-sm">
                                        <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                                            <div className="w-3 h-3 rounded-full bg-green-500"></div>
                                        </div>
                                        <div>
                                            <div className="font-medium text-gray-900">Currently Clocked In</div>
                                            <div className="text-sm text-gray-500">12 members active</div>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-4 bg-white rounded-lg p-4 shadow-sm">
                                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                                            <Shield className="w-5 h-5 text-primary" />
                                        </div>
                                        <div>
                                            <div className="font-medium text-gray-900">Duty Officer Active</div>
                                            <div className="text-sm text-gray-500">Incident Management</div>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-4 bg-white rounded-lg p-4 shadow-sm">
                                        <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                                            <Bell className="w-5 h-5 text-amber-600" />
                                        </div>
                                        <div>
                                            <div className="font-medium text-gray-900">Upcoming Training</div>
                                            <div className="text-sm text-gray-500">Flood Rescue - Tomorrow</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Mobile Section */}
            <section className="py-20 bg-gray-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        <div className="order-2 lg:order-1">
                            <div className="relative mx-auto w-64">
                                <div className="absolute -inset-4 bg-primary/10 rounded-3xl blur-xl"></div>
                                <div className="relative bg-gray-900 rounded-3xl p-2 shadow-2xl">
                                    <div className="bg-white rounded-2xl overflow-hidden">
                                        <div className="bg-primary p-4 text-white text-center">
                                            <div className="text-sm font-medium">Response Management System</div>
                                        </div>
                                        <div className="p-4 space-y-3">
                                            <div className="flex gap-2">
                                                <div className="flex-1 bg-primary text-white rounded-lg p-3 text-center text-sm font-medium">
                                                    Clock-on
                                                </div>
                                                <div className="flex-1 bg-purple-600 text-white rounded-lg p-3 text-center text-sm font-medium">
                                                    Duty Officer
                                                </div>
                                            </div>
                                            <div className="bg-green-50 border border-green-200 rounded-lg p-2 text-xs text-green-800">
                                                Currently clocked in
                                            </div>
                                            <div className="space-y-2">
                                                <div className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded">
                                                    <Calendar className="w-4 h-4 text-gray-400" />
                                                    <span className="text-sm">Calendar</span>
                                                </div>
                                                <div className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded">
                                                    <FileText className="w-4 h-4 text-gray-400" />
                                                    <span className="text-sm">Logbook</span>
                                                </div>
                                                <div className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded">
                                                    <GraduationCap className="w-4 h-4 text-gray-400" />
                                                    <span className="text-sm">Training</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="order-1 lg:order-2">
                            <div className="flex items-center gap-3 mb-4">
                                <Smartphone className="w-8 h-8 text-primary" />
                                <span className="text-sm font-medium text-primary uppercase tracking-wide">Mobile First</span>
                            </div>
                            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
                                Access Anywhere, Anytime
                            </h2>
                            <p className="text-lg text-gray-600 mb-6">
                                REACT QLD is designed mobile-first, ensuring your team can clock in, 
                                check schedules, and respond to incidents from anywhere. 
                                No app download required - works on any device with a web browser.
                            </p>
                            <ul className="space-y-3">
                                <li className="flex items-center gap-3 text-gray-700">
                                    <CheckCircle className="w-5 h-5 text-primary" />
                                    Works on phones, tablets, and desktops
                                </li>
                                <li className="flex items-center gap-3 text-gray-700">
                                    <CheckCircle className="w-5 h-5 text-primary" />
                                    No app installation needed
                                </li>
                                <li className="flex items-center gap-3 text-gray-700">
                                    <CheckCircle className="w-5 h-5 text-primary" />
                                    Instant push notifications
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-20 bg-primary">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
                        Ready to Transform Your Team Management?
                    </h2>
                    <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto">
                        Join other response teams already using REACT QLD 
                        to streamline their operations.
                    </p>
                    <Button 
                        size="lg"
                        onClick={() => navigate('/contact')}
                        className="bg-white text-primary hover:bg-gray-100 font-semibold px-10 py-6 text-lg"
                        data-testid="cta-contact-btn"
                    >
                        Get Started
                        <ArrowRight className="ml-2 w-5 h-5" />
                    </Button>
                </div>
            </section>

            {/* Footer */}
            <footer className="bg-gray-900 py-12">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                        <div className="flex items-center gap-3">
                            <img 
                                src="/react-qld-logo.png" 
                                alt="REACT QLD" 
                                className="h-12 object-contain"
                            />
                        </div>
                        <div className="flex items-center gap-6 text-gray-400 text-sm">
                            <a href="#features" className="hover:text-white transition-colors">Features</a>
                            <a href="#overview" className="hover:text-white transition-colors">Overview</a>
                            <a href="#benefits" className="hover:text-white transition-colors">Benefits</a>
                            <span 
                                onClick={() => navigate('/contact')} 
                                className="hover:text-white transition-colors cursor-pointer"
                            >
                                Contact Us
                            </span>
                            <span 
                                onClick={() => navigate('/privacy-policy')} 
                                className="hover:text-white transition-colors cursor-pointer"
                            >
                                Privacy Policy
                            </span>
                        </div>
                        <div className="text-gray-500 text-sm">
                            &copy; {new Date().getFullYear()} REACT QLD. Response Management System.
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
}
